// The ring sequencer that orbits the globe: 16 steps around, 8 lanes outward.
// Drawn with Canvas2D on a transparent overlay above the WebGL globe.

import { BAR_MS, LANES, STEPS, laneToMidi, midiName, type Harmony, type VoiceName } from "../shared/music";
import { cellAge, cellKey, type Cell } from "../shared/protocol";

export interface RingHit {
  step: number;
  lane: number;
}

const TAU = Math.PI * 2;
const STEP_ANGLE = TAU / STEPS;
const START = -Math.PI / 2;

interface Burst {
  step: number;
  lane: number;
  hue: number;
  born: number;
  kind: "place" | "remove";
}

export class RingView {
  readonly canvas: HTMLCanvasElement;
  private g: CanvasRenderingContext2D;
  private dpr = 1;
  cx = 0;
  cy = 0;
  r0 = 100;
  r1 = 200;
  hover: RingHit | null = null;
  /** Per-cell flash intensity, decays every frame. */
  private flashes = new Float32Array(STEPS * LANES);
  /** Per-step flash for the spokes. */
  private stepFlash = new Float32Array(STEPS);
  private bursts: Burst[] = [];
  private lastFrame = 0;
  /** 0..1 — how visible the ring is (hidden in "globe mode"). */
  visibility = 1;
  private targetVisibility = 1;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const g = canvas.getContext("2d");
    if (!g) throw new Error("Canvas2D unavailable");
    this.g = g;
  }

  resize(width: number, height: number): void {
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.round(width * this.dpr);
    this.canvas.height = Math.round(height * this.dpr);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
  }

  layout(cx: number, cy: number, r0: number, r1: number): void {
    this.cx = cx;
    this.cy = cy;
    this.r0 = r0;
    this.r1 = r1;
  }

  setVisible(v: boolean): void {
    this.targetVisibility = v ? 1 : 0;
  }

  get laneWidth(): number {
    return (this.r1 - this.r0) / LANES;
  }

  hit(x: number, y: number, slack = 0): RingHit | null {
    if (this.visibility < 0.5) return null;
    const dx = x - this.cx;
    const dy = y - this.cy;
    const r = Math.hypot(dx, dy);
    if (r < this.r0 - slack || r > this.r1 + slack) return null;
    const lane = Math.max(0, Math.min(LANES - 1, Math.floor((r - this.r0) / this.laneWidth)));
    let a = Math.atan2(dy, dx) - START;
    a = ((a % TAU) + TAU) % TAU;
    const step = Math.floor(a / STEP_ANGLE) % STEPS;
    return { step, lane };
  }

  /** Centre of a cell in CSS pixels. */
  cellCenter(step: number, lane: number): { x: number; y: number } {
    const a = START + (step + 0.5) * STEP_ANGLE;
    const r = this.r0 + (lane + 0.5) * this.laneWidth;
    return { x: this.cx + Math.cos(a) * r, y: this.cy + Math.sin(a) * r };
  }

  flash(step: number, lane: number, amount = 1): void {
    const k = cellKey(step, lane);
    this.flashes[k] = Math.max(this.flashes[k]!, amount);
    this.stepFlash[step] = Math.max(this.stepFlash[step]!, amount * 0.6);
  }

  burst(step: number, lane: number, hue: number, kind: "place" | "remove"): void {
    this.bursts.push({ step, lane, hue, born: performance.now(), kind });
    if (this.bursts.length > 64) this.bursts.shift();
  }

  private cellPath(step: number, lane: number, inset = 0): void {
    const g = this.g;
    const gapA = Math.min(0.035, 1.6 / this.r1) + inset / this.r1;
    const a0 = START + step * STEP_ANGLE + gapA;
    const a1 = START + (step + 1) * STEP_ANGLE - gapA;
    const w = this.laneWidth;
    const ri = this.r0 + lane * w + 1.2 + inset;
    const ro = this.r0 + (lane + 1) * w - 1.2 - inset;
    g.beginPath();
    g.arc(this.cx, this.cy, ro, a0, a1);
    g.arc(this.cx, this.cy, ri, a1, a0, true);
    g.closePath();
  }

  draw(opts: {
    now: number;
    serverNow: number;
    epoch: number;
    bar: number;
    cells: Map<number, Cell>;
    youId: string | null;
    harmony: Harmony;
    voice: VoiceName;
    energy: number;
  }): void {
    const { now, serverNow, epoch, bar, cells, youId, harmony } = opts;
    const dt = this.lastFrame ? Math.min(0.1, (now - this.lastFrame) / 1000) : 0.016;
    this.lastFrame = now;
    this.visibility += (this.targetVisibility - this.visibility) * Math.min(1, dt * 6);
    const decay = Math.exp(-dt * 5.5);
    for (let i = 0; i < this.flashes.length; i++) this.flashes[i]! *= decay;
    for (let i = 0; i < STEPS; i++) this.stepFlash[i]! *= decay;

    const g = this.g;
    g.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    g.clearRect(0, 0, this.canvas.width / this.dpr, this.canvas.height / this.dpr);
    const vis = this.visibility;
    if (vis < 0.01) return;
    g.save();
    g.globalAlpha = vis;
    const { cx, cy, r0, r1 } = this;
    const w = this.laneWidth;

    // Guide rings.
    g.lineWidth = 1;
    for (let l = 0; l <= LANES; l++) {
      const r = r0 + l * w;
      g.strokeStyle = l === 0 || l === LANES ? "rgba(160, 200, 255, 0.16)" : "rgba(160, 200, 255, 0.045)";
      g.beginPath();
      g.arc(cx, cy, r, 0, TAU);
      g.stroke();
    }
    // Spokes: brighter on the beat and when the step fires.
    for (let s = 0; s < STEPS; s++) {
      const a = START + s * STEP_ANGLE;
      const beat = s % 4 === 0;
      const f = this.stepFlash[s]!;
      g.strokeStyle = `rgba(170, 210, 255, ${(beat ? 0.14 : 0.05) + f * 0.25})`;
      g.beginPath();
      g.moveTo(cx + Math.cos(a) * (r0 - (beat ? 6 : 0)), cy + Math.sin(a) * (r0 - (beat ? 6 : 0)));
      g.lineTo(cx + Math.cos(a) * (r1 + (beat ? 6 : 0)), cy + Math.sin(a) * (r1 + (beat ? 6 : 0)));
      g.stroke();
    }

    // Empty cell markers.
    for (let s = 0; s < STEPS; s++) {
      const beat = s % 4 === 0;
      for (let l = 0; l < LANES; l++) {
        if (cells.has(cellKey(s, l))) continue;
        const c = this.cellCenter(s, l);
        g.fillStyle = `rgba(190, 220, 255, ${beat ? 0.2 : 0.11})`;
        g.beginPath();
        g.arc(c.x, c.y, Math.max(1, w * 0.07), 0, TAU);
        g.fill();
      }
    }

    // Playhead: a sweeping beam with a conic trail.
    const frac = ((((serverNow - epoch) % BAR_MS) + BAR_MS) % BAR_MS) / BAR_MS;
    const head = START + frac * TAU;
    if ("createConicGradient" in g) {
      const trail = 0.9;
      const grad = g.createConicGradient(head - trail, cx, cy);
      grad.addColorStop(0, "rgba(120, 200, 255, 0)");
      grad.addColorStop((trail / TAU) * 0.85, `rgba(120, 200, 255, ${0.05 + opts.energy * 0.06})`);
      grad.addColorStop(trail / TAU, `rgba(170, 230, 255, ${0.16 + opts.energy * 0.1})`);
      grad.addColorStop(trail / TAU + 0.0001, "rgba(120, 200, 255, 0)");
      grad.addColorStop(1, "rgba(120, 200, 255, 0)");
      g.fillStyle = grad;
      g.beginPath();
      g.arc(cx, cy, r1 + 4, 0, TAU);
      g.arc(cx, cy, r0 - 4, TAU, 0, true);
      g.fill();
    }
    g.strokeStyle = "rgba(200, 240, 255, 0.75)";
    g.lineWidth = 1.5;
    g.beginPath();
    g.moveTo(cx + Math.cos(head) * (r0 - 8), cy + Math.sin(head) * (r0 - 8));
    g.lineTo(cx + Math.cos(head) * (r1 + 10), cy + Math.sin(head) * (r1 + 10));
    g.stroke();

    // Cells.
    for (const c of cells.values()) {
      const k = cellKey(c.step, c.lane);
      const age = cellAge(c, bar);
      const f = this.flashes[k]!;
      const alpha = (1 - age * 0.75) * (0.55 + f * 0.45);
      const light = 55 + f * 25;
      this.cellPath(c.step, c.lane);
      g.fillStyle = `hsla(${c.hue}, 85%, ${light}%, ${alpha})`;
      g.fill();
      if (f > 0.05) {
        g.save();
        g.globalCompositeOperation = "lighter";
        g.shadowColor = `hsla(${c.hue}, 100%, 65%, ${f})`;
        g.shadowBlur = 18 * f;
        g.fillStyle = `hsla(${c.hue}, 100%, 75%, ${f * 0.6})`;
        this.cellPath(c.step, c.lane, -1);
        g.fill();
        g.restore();
      }
      if (c.owner === youId) {
        const p = this.cellCenter(c.step, c.lane);
        g.fillStyle = `rgba(255, 255, 255, ${0.85 - age * 0.5})`;
        g.beginPath();
        g.arc(p.x, p.y, Math.max(1.2, w * 0.09), 0, TAU);
        g.fill();
      }
    }

    // Place / remove bursts.
    this.bursts = this.bursts.filter((b) => now - b.born < 700);
    for (const b of this.bursts) {
      const t = (now - b.born) / 700;
      const p = this.cellCenter(b.step, b.lane);
      g.strokeStyle = b.kind === "place" ? `hsla(${b.hue}, 100%, 70%, ${1 - t})` : `rgba(255,255,255,${(1 - t) * 0.6})`;
      g.lineWidth = 2 * (1 - t) + 0.5;
      g.beginPath();
      g.arc(p.x, p.y, w * (0.3 + t * 1.6), 0, TAU);
      g.stroke();
    }

    // Hover.
    if (this.hover) {
      const { step, lane } = this.hover;
      this.cellPath(step, lane);
      g.strokeStyle = "rgba(255, 255, 255, 0.85)";
      g.lineWidth = 1.5;
      g.stroke();
      const midi = laneToMidi(lane, harmony, opts.voice);
      const c = this.cellCenter(step, lane);
      const outward = Math.atan2(c.y - cy, c.x - cx);
      const lx = cx + Math.cos(outward) * (r1 + 22);
      const ly = cy + Math.sin(outward) * (r1 + 22);
      g.font = "600 12px ui-sans-serif, system-ui, sans-serif";
      g.textAlign = "center";
      g.textBaseline = "middle";
      g.fillStyle = "rgba(230, 245, 255, 0.95)";
      g.fillText(midiName(midi), lx, ly);
      const existing = cells.get(cellKey(step, lane));
      if (existing) {
        g.font = "11px ui-sans-serif, system-ui, sans-serif";
        g.fillStyle = `hsla(${existing.hue}, 90%, 75%, 0.95)`;
        g.fillText(existing.owner === youId ? "you" : existing.ownerName, lx, ly + 15);
      }
    }
    g.restore();
  }
}
